<template>
  <div
    class="integration-card"
    @click="push(to)"
  >
    <div class="head">
      <UiComponentImage
        v-if="!!props.integration.logo"
        :alt="props.integration.title"
        class="logo"
        :expected-size="48"
        :height="props.integration.logo.height"
        :orientation="
          props.integration.logo.height > props.integration.logo.width
            ? 'landscape'
            : 'portrait'
        "
        :source="props.integration.logo.url"
        :width="props.integration.logo.width"
      />
      <div
        v-if="props.integration.tags.isEnterpriseOnly || props.integration.tags.isFeatured || props.integration.tags.isNew"
        class="tags"
      >
        <UiComponentTag
          v-if="props.integration.tags.isEnterpriseOnly"
          class="is-enterprise-only"
          size="s"
        >
          <UiComponentImage
            alt="enterprise only plan"
            :expected-size="16"
            :height="16"
            source="https://livestorm.imgix.net/1127/1693920279-enterprise-plan-only.svg"
            :width="16"
          />
          <UiComponentParagraph>
            <slot name="enterprise" />
          </UiComponentParagraph>
        </UiComponentTag>
        <UiComponentTag
          v-if="props.integration.tags.isFeatured"
          class="is-featured"
          size="s"
        >
          <UiComponentImage
            alt="featured"
            :expected-size="16"
            :height="16"
            source="https://livestorm.imgix.net/1127/1693920280-featured.svg"
            :width="16"
          />
          <UiComponentParagraph>
            <slot name="featured" />
          </UiComponentParagraph>
        </UiComponentTag>
        <UiComponentTag
          v-if="props.integration.tags.isNew"
          class="is-new"
          size="s"
        >
          <UiComponentImage
            alt="new"
            :expected-size="16"
            :height="16"
            source="https://livestorm.imgix.net/1127/1693920281-new.svg"
            :width="16"
          />
          <UiComponentParagraph>
            <slot name="new" />
          </UiComponentParagraph>
        </UiComponentTag>
      </div>
    </div>
    <div class="body">
      <NuxtLink :to="to">
        <UiComponentParagraph class="name">
          {{ props.integration.title }}
        </UiComponentParagraph>
      </NuxtLink>
      <UiComponentParagraph class="description">
        {{ props.integration.text }}
      </UiComponentParagraph>
      <UiComponentParagraph
        v-if="props.integration.categories && props.integration.categories.length > 0"
        class="categories"
      >
        {{ props.integration.categories.join(", ") }}
      </UiComponentParagraph>
    </div>
  </div>
</template>

<script setup>
// Composables
const localePath = useLocalePath()
const { push } = useRouter()

// Props
const props = defineProps({
  integration: {
    required: true,
    type: Object,
  },
})

// Computed Properties
const to = computed(() => {
  return localePath({
    name: 'integrations-slug',
    params: { slug: props.integration.slug },
  })
})
</script>

<style lang="postcss" scoped>
.integration-card {
  @apply relative flex flex-col w-full p-8 bg-white border-2 border-grey-blue-200 rounded-4xl cursor-pointer transform transition-all ease-out duration-300;

  &:hover {
    @apply border-white shadow-light4;
  }

  .head {
    @apply relative flex justify-between;

    .logo {
      @apply p-2 rounded-none;
    }

    .tags {
      @apply absolute top-0 right-0 flex flex-col;

      .is-enterprise-only,
      .is-featured,
      .is-new {
        @apply flex self-end mt-2;

        &:first-child {
          @apply mt-0;
        }

        picture {
          @apply mr-1 rounded-none;
        }

        :deep(p) {
          @apply font-medium text-xs;
        }
      }

      .is-enterprise-only {
        @apply bg-grey-blue-200;

        :deep(p) {
          @apply text-grey-blue-600;
        }
      }

      .is-featured {
        @apply bg-sunburst-red-200;

        :deep(p) {
          @apply text-sunburst-red-500;
        }
      }

      .is-new {
        @apply bg-winter-green-100;

        :deep(p) {
          @apply text-winter-green-600;
        }
      }
    }
  }

  .body {
    @apply flex flex-col mt-4;

    .name {
      @apply font-medium text-winter-green-900 text-xl;
    }

    .description {
      @apply mt-4 line-clamp-4 text-ellipsis text-sm text-grey-blue-600 whitespace-normal leading-6;
    }

    .categories {
      @apply w-full mt-4 font-medium text-livestorm-blue-700 text-xs leading-[22px];
    }
  }
}
</style>
